import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RelayChatProvider, useRunStore, type RunStore } from '@inference.engine/agentic-react';
import { ContextRing, latestContextUse } from '@inference.engine/agentic-ui/components';
import { AgenticUiText } from '@/shared/i18n/AgenticUiText';
import { notificationService } from '@/lib/services/notification.service';
import { useContextWindowFor } from '../model/useContextWindowFor';
import { compactConversation } from '../api/conversation-summaries.api';
import { testSessionRun } from '../model/test-sessions';
import { AGENT_BUILDER_QUERY_KEYS } from '../api/query-keys';

export interface ChatContextRingProps {
  runStore: RunStore;
  /** The conversation, once it exists: the ring then offers to summarize it. */
  chatId?: string;
  /** While a turn streams or waits on the person, nothing may be summarized. */
  busy?: boolean;
}

/**
 * How full the agent's context got on the conversation's latest turn, under the chat input. Only a
 * traced (debug) turn reports its steps, so on any other turn this draws nothing.
 */
export function ChatContextRing({ runStore, chatId, busy }: ChatContextRingProps) {
  return (
    <RelayChatProvider store={runStore}>
      <AgenticUiText>
        <LatestContextRing chatId={chatId} busy={busy === true} />
      </AgenticUiText>
    </RelayChatProvider>
  );
}

function LatestContextRing({ chatId, busy }: { chatId: string | undefined; busy: boolean }) {
  const { t } = useTranslation('agentBuilder');
  const queryClient = useQueryClient();
  const compact = useMutation({
    // Summarized by the build its turns run on: a test of a draft by the draft.
    mutationFn: (contextId: string) =>
      compactConversation(contextId, testSessionRun(contextId)?.draft === true),
    onSuccess: (_, contextId) =>
      queryClient.invalidateQueries({ queryKey: AGENT_BUILDER_QUERY_KEYS.summaries(contextId) }),
    onError: () => notificationService.showError(t('testDock.compactFailed')),
  });
  const runs = useRunStore((s) => s.runs);
  const runOrder = useRunStore((s) => s.runOrder);
  const nodes = useRunStore((s) => s.nodes);
  const handoffs = useRunStore((s) => s.handoffs);
  const guardrails = useRunStore((s) => s.guardrails);
  const trace = useRunStore((s) => s.trace);
  const contextWindowFor = useContextWindowFor();
  const context = useMemo(
    () =>
      latestContextUse(
        { runs, runOrder, nodes, handoffs, guardrails, trace },
        { contextWindowFor },
      ),
    [runs, runOrder, nodes, handoffs, guardrails, trace, contextWindowFor],
  );
  // The add button's box, so the footer's two ends sit the same distance from the input's edges.
  return context ? (
    <span className="flex size-8 items-center justify-center">
      <ContextRing
        context={context}
        {...(chatId
          ? {
              onCompact: () => compact.mutate(chatId),
              compactDisabled: busy,
              compacting: compact.isPending,
            }
          : {})}
      />
    </span>
  ) : null;
}
